<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useSortable } from '@vueuse/integrations/useSortable'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import PageHeader from '@lib/components/shared/PageHeader.vue'
import BBtn from '@lib/components/base/BBtn.vue'
import Checkbox from 'primevue/checkbox'
import QrPreview from '@lib/components/shared/QrPreview.vue'
import ClipboardCard from '@/components/domain/clipboard/ClipboardCard.vue'
import ClipboardPageBreak from '@/components/domain/clipboard/ClipboardPageBreak.vue'
import ClipboardAddPageButton from '@/components/domain/clipboard/ClipboardAddPageButton.vue'
import ClipboardServiceCard from '@/components/domain/clipboard/ClipboardServiceCard.vue'
import ClipboardServiceCatalog from '@/components/domain/clipboard/ClipboardServiceCatalog.vue'
import ClipboardTicketCard from '@/components/domain/clipboard/ClipboardTicketCard.vue'
import { User, Wrench } from 'lucide-vue-next'
import { useUiStore } from '@/stores/ui'
import { useToast } from '@lib/composables/useToast'
import { useAuthStore } from '@/stores/auth'
import { useFormatters } from '@lib/composables/useFormatters'
import { useClipboardStore, type ClipboardCode, type ClipboardService, type ClipboardTicket } from '@/stores/clipboard'
import { buildTicketsetsCode, buildZonesCode, buildLoginCode, joinCommands } from '@/lib/configCode'
import { renderServiceCode } from '@/lib/serviceCode'
import { buildDeepLink, isDeepLinkable } from '@/lib/deepLink'

const { t } = useI18n()
const ui = useUiStore()
const toast = useToast()
const auth = useAuthStore()
const { dateTime } = useFormatters()
const clipboard = useClipboardStore()

const gridRef = ref<HTMLElement | null>(null)
const serviceCatalogOpen = ref(false)

// Timestamp of the moment the user clicked print — stamped per print so the
// printed sheet header shows when it was generated. Filled in printSheet().
const printedAt = ref<string>('')


onMounted(() => {
    ui.setBreadcrumbs([{ label: t('clipboard.title') }])
})

useSortable(gridRef, clipboard.items, {
    animation: 150,
    // Sortable only starts on the small handle in each card / page-break;
    // a drag from anywhere else on a card is native HTML5 DnD = merge.
    // The two systems would otherwise fight over the same pointer gesture
    // (we tried it; Sortable reorders the DOM before native dragenter even
    // fires, which broke merge entirely).
    handle: '.clipboard-card-handle, .clipboard-page-break-handle',
    filter: '.clipboard-page-title-input', // don't start drag from a title input
    preventOnFilter: false,
    ghostClass: 'clipboard-ghost',
    chosenClass: 'clipboard-chosen',
    dragClass: 'clipboard-drag',
    onUpdate: (evt: { oldIndex?: number; newIndex?: number }) => {
        if (evt.oldIndex !== undefined && evt.newIndex !== undefined) {
            clipboard.move(evt.oldIndex, evt.newIndex)
        }
    }
})

function displayTitleForPageBreak(pageBreakId: number): string {
    const idx = clipboard.pages.findIndex(p => p.pageBreakId === pageBreakId)
    if (idx === -1) return ''
    const page = clipboard.pages[idx]
    return page.title || t('clipboard.pageBreak.placeholder', { n: idx + 1 })
}

// Implicit-first-page header: only render when the clipboard starts with codes
// (not a page-break). Lets the user name the first page before any explicit
// page-break exists. We use id=0 as a sentinel that the edit handler converts
// into an `insertPageBreak(-1, title)` to materialize the implicit page.
const IMPLICIT_HEADER_ID = 0

const implicitHeader = computed(() => {
    const first = clipboard.items[0]
    if (!first || first.kind === 'page-break') return null
    return {
        id: IMPLICIT_HEADER_ID,
        kind: 'page-break' as const,
        title: ''
    }
})

const implicitHeaderDisplayTitle = computed(() =>
    t('clipboard.pageBreak.placeholder', { n: 1 })
)

function onAddPage() {
    clipboard.insertPageBreak()
}

function onRemove(id: number) {
    if (id === IMPLICIT_HEADER_ID) return // nothing to remove from store
    clipboard.remove(id)
}

function onSplit(id: number) {
    clipboard.split(id)
}

function onUpdatePrintLabel(id: number, label: string) {
    clipboard.setPrintLabel(id, label)
}

function onUpdateTitle(id: number, title: string) {
    if (id === IMPLICIT_HEADER_ID) {
        // Materialize the implicit first page by inserting a real page-break at
        // the very front with the user-typed title. If the title is empty there
        // is nothing to persist — keep the header purely virtual.
        if (title) clipboard.insertPageBreak(-1, title)
        return
    }
    clipboard.setPageTitle(id, title)
}

function onMergeRequest(sourceId: number, targetId: number) {
    const source = clipboard.items.find(it => it.id === sourceId)
    const result = clipboard.merge(sourceId, targetId)
    if (result.ok) {
        if (source && source.kind === 'login') toast.success(t('clipboard.merge.loginAttached'))
        else toast.success(t('clipboard.merge.success'))
        return
    }
    if (result.reason === 'kind-mismatch') {
        const target = clipboard.items.find(it => it.id === targetId)
        if (source?.kind === 'login' && target?.kind === 'login') {
            toast.error(t('clipboard.merge.deniedSameLogin'))
        } else {
            toast.error(t('clipboard.merge.deniedKindMismatch'))
        }
    }
    // 'not-mergeable' reasons (self-drop, page-break) are intentionally silent.
}

function renderRawPayload(code: ClipboardCode | ClipboardService | ClipboardTicket): string {
    if (code.kind === 'service') return renderServiceCode(code.service)
    if (code.kind === 'ticket') return code.code
    const loginPart = code.login ? buildLoginCode(code.login.username, code.login.password) : ''
    if (code.kind === 'login') return loginPart
    const payload =
        code.kind === 'ticketsets'
            ? buildTicketsetsCode(code.items.map(i => i.id))
            : buildZonesCode(code.items.map(i => i.id))
    return joinCommands([loginPart, payload])
}

// QR payload pro <QrPreview>: deep-linkovatelné kódy obalíme do URL, ostatní
// (HW-only servisní, vstupenky) zůstanou holé. Když je vypnutý přepínač
// "URL do QR kódu", neobaluje se nic — všechny kódy jsou holé.
function qrPayload(code: ClipboardCode | ClipboardService | ClipboardTicket): string {
    const raw = renderRawPayload(code)
    if (!clipboard.urlInQr) return raw
    return isDeepLinkable(code) ? buildDeepLink(raw) : raw
}

// Label shown next to the QR in the print mirror. For service codes the
// dedicated card renders its own icon+label combo, but here we have a flat
// label-row layout shared with ticket codes, so we resolve the service label
// to a static i18n string per kind/variant. Reads from clipboard.service.* keys.
function labelFor(code: ClipboardCode | ClipboardService | ClipboardTicket): string {
    if (code.kind === 'service') {
        const s = code.service
        switch (s.kind) {
            case 'server':       return t(`clipboard.service.server.${s.variant}`)
            case 'counter':      return s.variant === 1
                ? t('clipboard.service.counter.on')
                : t('clipboard.service.counter.off')
            case 'display':      return t('clipboard.service.display.label', { pct: s.variant })
            case 'language':     return t(`clipboard.service.language.${s.variant}`)
            case 'scan-info':    return t(`clipboard.service.scanInfo.${s.variant.toLowerCase()}`)
            case 'wifi':         return t('clipboard.service.wifi.label', { ssid: s.variant.ssid })
            case 'start-update': return t('clipboard.service.startUpdate.label')
            case 'reset':        return t('clipboard.service.reset.label')
        }
    }
    if (code.kind === 'ticket') return code.printLabel ?? code.code
    if (code.kind === 'login') return ''
    // Custom print label wins over the item-name list on the printed sheet.
    if (code.printLabel) return code.printLabel
    return code.items.map(i => i.name).join(', ') || `#${code.id}`
}

function loginUsernameFor(code: ClipboardCode | ClipboardService | ClipboardTicket): string | null {
    if (code.kind === 'service' || code.kind === 'ticket') return null
    return code.login?.username ?? null
}

async function printSheet() {
    printedAt.value = dateTime(new Date())
    // Wait for the print mirror to render the new timestamp before opening the
    // print dialog — window.print() is synchronous and would otherwise capture
    // the DOM before Vue flushes the printedAt update.
    await nextTick()
    window.print()
}
</script>

<template>
    <DefaultLayout>
        <PageHeader :title="t('clipboard.title')">
            <template #actions>
                <label class="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none mr-1">
                    <Checkbox v-model="clipboard.urlInQr" :binary="true" input-id="clipboard-url-in-qr" />
                    <span>{{ t('clipboard.urlInQr') }}</span>
                </label>
                <BBtn severity="secondary" @click="serviceCatalogOpen = true">
                    <Wrench class="w-4 h-4 mr-1" />
                    {{ t('clipboard.service.addButton') }}
                </BBtn>
                <ClipboardAddPageButton @add="onAddPage" />
                <BBtn severity="secondary" :disabled="!clipboard.count" @click="clipboard.clear()">
                    {{ t('clipboard.clear') }}
                </BBtn>
                <BBtn :disabled="!clipboard.count" @click="printSheet">
                    {{ t('clipboard.print') }}
                </BBtn>
            </template>
        </PageHeader>

        <div v-if="!clipboard.items.length" class="bg-white border border-gray-200 rounded-12 p-6 text-gray-500">
            {{ t('clipboard.empty') }}
        </div>

        <ClipboardPageBreak
            v-if="implicitHeader"
            :page="implicitHeader"
            :display-title="implicitHeaderDisplayTitle"
            :removable="false"
            @remove="onRemove"
            @update-title="onUpdateTitle"
        />

        <div
            ref="gridRef"
            class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3"
        >
            <template v-for="item in clipboard.items" :key="item.id">
                <ClipboardPageBreak
                    v-if="item.kind === 'page-break'"
                    :page="item"
                    :display-title="displayTitleForPageBreak(item.id)"
                    class="col-span-full"
                    @remove="onRemove"
                    @update-title="onUpdateTitle"
                />
                <ClipboardServiceCard
                    v-else-if="item.kind === 'service'"
                    :code="item"
                    @remove="onRemove"
                />
                <ClipboardTicketCard
                    v-else-if="item.kind === 'ticket'"
                    :code="item"
                    @remove="onRemove"
                    @update-print-label="onUpdatePrintLabel"
                />
                <ClipboardCard
                    v-else
                    :code="item"
                    @remove="onRemove"
                    @split="onSplit"
                    @merge-request="onMergeRequest"
                    @update-print-label="onUpdatePrintLabel"
                />
            </template>
        </div>

        <ClipboardServiceCatalog v-model="serviceCatalogOpen" />
    </DefaultLayout>

    <Teleport to="body">
        <div class="clipboard-print-root">
            <section
                v-for="(page, pageIdx) in clipboard.pages"
                :key="page.pageBreakId ?? `implicit-${pageIdx}`"
                class="clipboard-print-page"
            >
                <div class="clipboard-print-page-header">
                    <h2 class="clipboard-print-page-title">
                        {{ page.title || t('clipboard.pageBreak.placeholder', { n: pageIdx + 1 }) }}
                    </h2>
                    <div v-if="auth.activeInstance?.name || printedAt" class="clipboard-print-meta">
                        <div v-if="auth.activeInstance?.name" class="clipboard-print-instance">
                            {{ auth.activeInstance.name }}
                        </div>
                        <div v-if="printedAt" class="clipboard-print-date">{{ printedAt }}</div>
                    </div>
                </div>
                <div v-if="!page.codes.length" class="clipboard-print-empty">
                    {{ t('clipboard.pageBreak.empty') }}
                </div>
                <div v-else class="clipboard-print-grid">
                    <div v-for="code in page.codes" :key="code.id" class="qr-cell">
                        <QrPreview :code="qrPayload(code)" label="" :size="180" />
                        <div class="text-sm font-semibold inline-flex items-center gap-1 text-center break-words">
                            <User v-if="loginUsernameFor(code)" class="w-3.5 h-3.5 shrink-0 text-ink/70" />
                            <span v-if="loginUsernameFor(code)" class="text-ink/70 font-medium">
                                {{ loginUsernameFor(code) }}
                            </span>
                            <span v-if="loginUsernameFor(code) && labelFor(code)" class="text-gray-400">·</span>
                            <span v-if="labelFor(code)">{{ labelFor(code) }}</span>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    </Teleport>
</template>

<style>
.clipboard-ghost {
    opacity: 0.4;
    background: rgba(56, 189, 248, 0.1) !important;
}

.clipboard-chosen {
    box-shadow: 0 0 0 2px rgb(14 165 233 / 0.4);
}

.clipboard-drag {
    opacity: 0.9;
}

.clipboard-print-root {
    display: none;
}

@media print {
    body * {
        visibility: hidden;
    }
    .clipboard-print-root,
    .clipboard-print-root * {
        visibility: visible;
    }
    .clipboard-print-root {
        display: block;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        background: #fff;
    }
    .clipboard-print-page {
        padding: 10mm;
    }
    .clipboard-print-page + .clipboard-print-page {
        page-break-before: always;
    }
    .clipboard-print-page-header {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 8mm;
        margin-bottom: 0;
    }
    .clipboard-print-page-title {
        font-size: 14pt;
        font-weight: 600;
        margin: 0;
    }
    .clipboard-print-meta {
        text-align: right;
        flex-shrink: 0;
    }
    .clipboard-print-instance {
        font-size: 11pt;
        font-weight: 600;
    }
    .clipboard-print-date {
        font-size: 9pt;
        color: #555;
        margin-top: 0.5mm;
    }
    .clipboard-print-empty {
        color: #666;
        font-style: italic;
    }
    .clipboard-print-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        /* Sloupcová mezera ponechána; řádková (vertikální mezera pod textem
           kódu) zmenšena na 6mm. */
        column-gap: 12mm;
        row-gap: 6mm;
    }
    .clipboard-print-grid .qr-cell {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 4px;
        break-inside: avoid;
        page-break-inside: avoid;
    }
    html,
    body {
        background: #fff !important;
    }
    @page {
        margin: 0;
    }
}
</style>
